import { Extension } from "@verity/proctoring-option/types";
import { useState } from "react";
import { useMobileLayout } from "../../../hooks";
import styles from "./AddExtensionModal.module.css";
import { CoreModal, CoreModalDescription, CoreTextField } from "../../../core";
import i18nPackage from "../../../i18n/i18n";
import { Trans } from "react-i18next";
import { FormControlLabel, Link, Typography } from "@mui/material";

export default function AddExtensionNameModal({
	setShowAddModal,
	onAddExtension,
	fetchChromeExtensionData,
}: {
	setShowAddModal: React.Dispatch<React.SetStateAction<boolean>>;
	onAddExtension: (extension: Extension) => void;
	fetchChromeExtensionData: (id: string) => Promise<Extension | null>;
}) {
	const [extensionId, setExtensionId] = useState("");
	const [extracting, setExtracting] = useState<boolean>(false);

	const [idError, setIdError] = useState(false);
	const isMobileLayout = useMobileLayout();

	const extractExtensionId = (input: string): string | null => {
		// If it's already a valid 32-char lowercase ID, return it
		if (/^[a-p]{32}$/.test(input)) {
			return input;
		}

		// Try to extract from a Chrome Webstore URL, falling back to a
		// chrome://extensions/?id=<id> link
		const match =
			input.match(/\/detail\/[^/]+\/([a-p]{32})/) ?? input.match(/^chrome:\/\/extensions\/\?id=([a-p]{32})$/);

		return match ? match[1] : null;
	};

	return (
		<CoreModal
			open
			onClose={() => setShowAddModal(false)}
			title={i18nPackage.t(`editQuizPage.configurationStep.addExtensionModal.title`)}
			minWidth={isMobileLayout ? "100%" : "min(60%, 600px)"}
			widthFixed
			hideHorizontalOverflow
			hideVerticalOverflow
			contentMatchModalHeight
			padding={isMobileLayout ? "10px 24px" : undefined}
			buttons={[
				{
					text: i18nPackage.t(`editQuizPage.configurationStep.addExtensionModal.cancel`),
					color: "secondary",
					onClick: () => setShowAddModal(false),
				},
				{
					text: i18nPackage.t(`editQuizPage.configurationStep.addExtensionModal.add`),
					color: "primary",
					loading: extracting,
					onClick: async () => {
						try {
							setExtracting(true);
							const extractedId = extractExtensionId(extensionId);

							if (!extractedId) {
								throw Error("No ID");
							}

							const extension_info = await fetchChromeExtensionData(extractedId);
							if (!extension_info || extension_info.name === "Chrome Web Store") {
								throw Error("Invalid Extension Info");
							}

							onAddExtension({
								id: extension_info.id,
								name: extension_info.name,
								iconSvg: extension_info.iconSvg,
								type: "Other",
							});
							setShowAddModal(false);
						} catch (e) {
							console.error(e);
							setIdError(true);
						} finally {
							setExtracting(false);
						}
					},
					disabled: extensionId === "" || extracting,
				},
			]}
		>
			<CoreModalDescription>
				<Trans
					i18n={i18nPackage}
					i18nKey="editQuizPage.configurationStep.addExtensionModal.description"
					components={{
						a: (
							<Link
								href="https://support.yuja.com/hc/en-us/articles/36314216257943-Locating-a-Browser-Extension-URL"
								target="_blank"
								rel="noopener noreferrer"
								className={styles.applicationLink}
								aria-label={i18nPackage.t("editQuizPage.configurationStep.addExtensionModal.linkAriaLabel")}
							/>
						),
					}}
				/>
			</CoreModalDescription>

			<FormControlLabel
				control={
					<CoreTextField
						value={extensionId}
						onChange={(e) => {
							setExtensionId(e.target.value.trim());
							setIdError(false);
						}}
						sx={{ width: "100%" }}
						onPaste={(event) => {
							event.preventDefault();
							const pasted = event.clipboardData.getData("text").trim();
							setExtensionId(pasted);
							setIdError(false);
						}}
						placeholder={i18nPackage.t(`editQuizPage.configurationStep.addExtensionModal.inputPlaceholder`)}
						error={
							idError && extensionId.trim() !== ""
								? i18nPackage.t("editQuizPage.configurationStep.addExtensionModal.idErrorMsg")
								: undefined
						}
					/>
				}
				label={
					<Typography className={styles.addExtensionLabel}>
						{i18nPackage.t(`editQuizPage.configurationStep.addExtensionModal.inputTitle`)}
					</Typography>
				}
				className={styles.addExtensionFormControl}
				labelPlacement="top"
			/>
		</CoreModal>
	);
}
